
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface PromptModalProps {
    isOpen: boolean;
    title: string;
    message?: string;
    placeholder?: string;
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
    isDestructive?: boolean;
    onConfirm: (value: string) => void;
    onCancel: () => void;
}

const PromptModal: React.FC<PromptModalProps> = ({
                                                     isOpen,
                                                     title,
                                                     message,
                                                     placeholder = "Enter text...",
                                                     confirmText = "Confirm",
                                                     cancelText = "Cancel",
                                                     isLoading = false,
                                                     isDestructive = false,
                                                     onConfirm,
                                                     onCancel
                                                 }) => {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (isOpen) setValue('');
    }, [isOpen]);

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isLoading) {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isLoading, onCancel]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (value.trim()) onConfirm(value);
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[90] p-4" onClick={!isLoading ? onCancel : undefined}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {title}
                        </h3>
                        {message && (
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                {message}
                            </p>
                        )}
                        <input
                            autoFocus
                            type="text"
                            className="mt-4 w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white"
                            placeholder={placeholder}
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-transparent rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            {cancelText}
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !value.trim()}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all active:scale-95 flex items-center justify-center min-w-[80px] disabled:bg-gray-400 dark:disabled:bg-gray-600 ${
                                isDestructive
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-brand-600 hover:bg-brand-700'
                            }`}
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : confirmText}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PromptModal;
